import React, { useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { AgentChatMessage } from './types';

interface AgentChatMessagesProps {
  messages: AgentChatMessage[];
  isSending: boolean;
  streamingText: string;     // assistant text streamed so far this turn (Phase 3)
  activity: string | null;   // current tool activity label, e.g. "Saving 4.1…"
}

// Presentational message list. Auto-scrolls to the bottom on new content.
// While a turn streams, the last bubble shows live text (or a thinking indicator before the
// first token) plus the current tool-activity chip.
const AgentChatMessages: React.FC<AgentChatMessagesProps> = ({ messages, isSending, streamingText, activity }) => {
  const bottomRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, isSending, streamingText, activity]);

  return (
    <div className="agent-chat-messages">
      {messages.length === 0 && !isSending && (
        <div className="agent-chat-intro">
          <p>
            Tell me about your study and I'll help you draft the narrative sections of your
            protocol — the experimental design, animal model, abstract, adverse-effects plan,
            and so on. Yes/no questions and numbers are entered directly in InfoEd, so we'll
            focus on the writing.
          </p>
          <p>
            A good place to start: tell me your species and the basic question — or paste the
            Approach / experimental design section from your grant (just that section, not the
            whole application).
          </p>
        </div>
      )}

      {messages.map((m, i) => (
        <div key={i} className={`agent-msg ${m.role}`}>
          <div className="agent-msg-bubble">
            {m.role === 'assistant' ? (
              <ReactMarkdown>{m.text}</ReactMarkdown>
            ) : (
              <span className="agent-msg-text">{m.text}</span>
            )}
            {m.saves && m.saves.length > 0 && (
              <div className="agent-msg-saves">
                {m.saves.map((s) => (
                  <span key={s.question_number} className="agent-save-chip">
                    ✓ Saved {s.question_number}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}

      {isSending && (
        <div className="agent-msg assistant">
          <div className="agent-msg-bubble">
            {streamingText ? (
              <div className="agent-msg-streaming">
                <ReactMarkdown>{streamingText}</ReactMarkdown>
                <span className="agent-stream-cursor" aria-hidden="true" />
              </div>
            ) : (
              <span className="agent-typing" aria-label="Assistant is thinking">
                <span></span><span></span><span></span>
              </span>
            )}
            {activity && (
              <div className="agent-activity">
                <span className="agent-activity-spinner" aria-hidden="true" />
                {activity}
              </div>
            )}
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
};

export default AgentChatMessages;
